import { gql } from "@apollo/client";
import { accProjectDetailsType, countryType, geloLocationyType, imageType, mapLocationType } from "./graphql/constants";

const productPropertyType = `
  id
  label
  organizationId
  displaySection
  displayOrder
  type
  subType
  categoryIds
  icon
  info
  extras {
    unit
  }
  validations {
    supportedFileTypes
    maxUploadFileSize
    maxUploadFileQuantity
    required
    max
    unselectable
    multiple
    linkedProperties
  }
`;

const propertySettings = `
  settings {
    lod
    standard
    instruction
    visibility
  }
`;

export const GET_PRODUCT_PROPERTIES = gql`
  query {
    getProductProperties {
      ${productPropertyType}
      
    }
  }
`;

export const GET_PRODUCT_PROPERTY_VARIANTS = gql`
  query {
    getProductPropertyVariants {
      ${productPropertyType}
      productPropertyId
      ${propertySettings}
    }
  }
`;

export const GET_TRANSLATIONS = gql`
  query ($organizationId: ID!) {
    getTranslations(organizationId: $organizationId) {
      id
      label
      displayName
      type
      organizationId
    }
  }
`;

export const GET_PROPERTY_VALUE_OPTIONS = gql`
  query {
    getPropertyValueOptions {
      id
      displayName
      propertyIds
      displaySection
      displayOrder
      organizationId
      additionalColumnValues {
        id
        value
      }
      metadata {
        key
        value
      }
    }
  }
`;

export const GET_DISPLAY_SECTIONS = gql`
  query {
    getDisplaySections {
      id
      displayName
      propertyIds
      categoryIds
      displayOrder
      organizationId
      type
      icon
      settings {
        addProperty
        manageVisibility
      }
    }
  }
`;

export const CREATE_PRODUCT_DETAIL = gql`
  mutation createProduct($input: ProductInput!) {
    createProduct(input: $input) {
      status
      message
      data {
        id
        organizationId
        description
        categories {
          id
          displayName
          parentCategoryId
        }
        brand {
          id
          displayName
        }
        distributors {
          id
          displayName
        }
        status
        createdAt
        createdBy
        updatedAt
        updatedBy
        properties {
          id
          value
          values
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT_DETAIL = gql`
  mutation updateProduct($id: ID!, $input: ProductInput!) {
    updateProduct(id: $id, input: $input) {
      status
      message
    }
  }
`;

export const GET_USER = gql`
  query {
    getUser {
      id
      displayName
      email
      emailVerified
      organizations {
        id
        displayName
        roles
        houses {
          id
          displayName
        }
      }
      photoUrl
      marketing {
        bimmatch
        products
      }
      profession
    }
  }
`;

export const GET_ORGANIZATION = gql`
  query {
    getOrganization {
      id
      displayName
      owner
      websiteUrl
      brandId
      subscription {
        plan
      }
      aiProcessing {
        enabled
      }
      logo {
        ${imageType}
      }
      contact {
        name
        email
        phone
        role
      }
      address
      members {
        id
        roles
      }

      mapLocation {
        ${mapLocationType}
      }

      geoLocation {
        ${geloLocationyType}
      }

      accDetails {
        userId
      }
      usage {
        accProjects
        bimProjects
        products
        rfaUploads
        rvtUploads
      specs
      }  
    }
  }
`;

export const DELETE_PRODUCT = gql`
  mutation deleteProduct($id: ID!) {
    updateProduct(id: $id, input: { status: ARCHIVE }) {
      data {
        id
        displayName
        status
      }
    }
  }
`;

export const DUPLICATE_PRODUCT = gql`
  mutation duplicateProduct($id: ID!) {
    duplicateProduct(id: $id) {
      data {
        id
        displayName
        status
        categories {
          parentCategoryId
          id
          displayName
        }
      }
    }
  }
`;

export const CREATE_PRODUCT_PROPERTY = gql`
  mutation CreateProductProperty($input: [PropertyInput]!) {
    createProductProperty(input: $input) {
      ${productPropertyType}
    }
  }
`;

export const UPDATE_PRODUCT_PROPERTY = gql`
  mutation UpdateProductProperty($input: [PropertyInput]) {
    updateProductProperty(input: $input) {
      ${productPropertyType}
      productPropertyId
      ${propertySettings}
    }
  }
`;

export const DELETE_PRODUCT_PROPERTY = gql`
  mutation DeleteProductProperty($id: ID!) {
    deleteProductProperty(id: $id) {
      status
      message
    }
  }
`;

export const UPDATE_PROPERTY_VALUE_OPTION = gql`
  mutation UpdatePropertyValueOption($input: [PropertyValueOptionInput]!) {
    updatePropertyValueOption(input: $input) {
      status
      message
    }
  }
`;

export const DELETE_PROPERTY_VALUE_OPTION = gql`
  mutation DeletePropertyValueOption($id: ID!) {
    deletePropertyValueOption(id: $id) {
      status
      message
    }
  }
`;

export const SEND_RFQ_EMAIL = gql`
  mutation SendRequestForQuote($input: RequestForQuoteInput!) {
    sendRequestForQuote(input: $input) {
      status
      message
    }
  }
`;

const commonGetProdQuery = `
      id
      displayName
      description
      images {${imageType}}
      files {
        id
        values {${imageType}}
      }
      properties {
        id
        value
        values
      }
`;

export const GET_PRODUCT_DETAIL = gql`
  query ($id: ID!) {
    getProduct(id: $id) {
      ${commonGetProdQuery}
      organizationId
      organizationName
      organizationPlan
      organizationBrandId
      source
      mainCategory
      houses
      organizationLogo {
        ${imageType}
      }
      types {
        ${commonGetProdQuery}
        createdAt
      }
      status
      distributors {
        id
        displayName
        mapLocation {
          lat
          lng
        }
        contact {
          address
        }
      }
      categories {
        id
        displayName
        parentCategoryId
      }
      brand {
        id
        displayName
      }
      updatedAt
      createdAt
      types {
        id
        displayName
        description
        properties {
          id
          value
          values
        }
        files {
          id
          values {
            ${imageType}
          }
        }
        images {
          ${imageType}
        }
      }
    }
  }
`;

export const UPDATE_ORGANIZATION_DATA = gql`
  mutation updateOrganization($id: ID!, $input: OrganizationInput!) {
    updateOrganization(id: $id, input: $input) {
      status
      message
    }
  }
`;

export const CREATE_PRODUCT_FROM_FILE_JOB = gql`
  mutation createProductFromFileJob(
    $id: ID!
    $jobId: ID!
    $type: JobType!
    $files: [FileLinkInput]!
  ) {
    createProductFromFileJob(id: $id, jobId: $jobId, type: $type, files: $files) {
      status
      message
    }
  }
`;

//Members APIs
export const GET_TEAM_DETAILS = gql`
  query {
    getTeamDetails {
      houses {
        id
        displayName
      }
      owner {
        id
        name
        email
        photoUrl
        joinedAt
        houses
      }

      invitedMembers
      members {
        id
        name
        email
        photoUrl
        joinedAt
        houses
        roles
      }
    }
  }
`;

// ---- Personal Profile ------
export const UPDATE_USER = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      status
      message
    }
  }
`;

//--- Team or project invitation ---
export const INVITE_MEMBER = gql`
  mutation InviteMember($email: String!, $type: String!, $houseId: ID) {
    inviteMember(email: $email, type: $type, houseId: $houseId) {
      status
      message
    }
  }
`;

export const ADD_MEMBER = gql`
  mutation AddMember($token: String!) {
    addMember(token: $token) {
      status
      message
      houseId
      userOrganization {
        id
        displayName
        roles
        subscription {
          plan
        }
      }
    }
  }
`;

export const REMOVE_MEMBER = gql`
  mutation RemoveMember(
    $memberId: String
    $type: String!
    $email: String
    $houseId: String
  ) {
    removeMember(
      memberId: $memberId
      type: $type
      email: $email
      houseId: $houseId
    ) {
      status
      message
    }
  }
`;

export const UPDATE_TEAM_DETAILS = gql`
  mutation UpdateTeamDetails($input: [MemberInput]!) {
    updateTeamDetails(input: $input) {
      status
      message
    }
  }
`;

//--- XXX ---

export const CREATE_PROJECT = gql`
  mutation CreateHouse($input: HouseInput!) {
    createHouse(input: $input) {
      id
    }
  }
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateHouse($id: ID!, $input: HouseInput!) {
    updateHouse(id: $id, input: $input) {
      id
      displayName
      location
      country {
       ${countryType}
      }
      images {
        location
        displayName
        type
        Key
      }
      createdAt
      typeOfProject
      constructionDate
      matchEngineSettings {
        locations
        standards
      }
    }
  }
`;

export const GET_HUBS = gql`
  query {
    getHubs {
      id
      displayName
      region
      type
    }
  }
`;

export const GET_ACC_ACCOUNT_PROJECTS = gql`
  query ($hubId: String!) {
    getProjects(hubId: $hubId) {
      id
      displayName
      type
    }
  }
`;

export const GET_ACC_FOLDER_CONTENTS = gql`
  query GetFolderContents(
    $hubId: String!
    $projectId: String!
    $folderId: String
  ) {
    getFolderContents(
      hubId: $hubId
      projectId: $projectId
      folderId: $folderId
    ) {
      displayName
      id
      type
    }
  }
`;

export const GET_REVIT_FILE_VERSION = gql`
  query Query($projectId: String!, $fileId: String!) {
    getRevitFileVersion(projectId: $projectId, fileId: $fileId) {
      cloudModelGuid
      cloudProjectGuid
      model
      projectType
    }
  }
`;

export const SYNC_ACC_PROJECT = gql`
  mutation SyncAccProject($houseId: String!) {
    syncAccProject(houseId: $houseId) {
      status
      message
    }
  }
`;

export const CONNECT_TO_ACC = gql`
  mutation ConnectToAcc($token: String!) {
    connectToAcc(token: $token) {
      status
      message
    }
  }
`;

export const GET_OBJECT_HISTORY = gql`
  query ($houseId: String!, $objectId: Int!) {
    getObjectHistory(houseId: $houseId, objectId: $objectId) {
      createdAt
      action
      productId
      userId
      productName
      userName
      productImg
      userProfileImg
      objectId
    }
  }
`;

export const GET_HOUSES = gql`
  query GetHouses($after: String, $first: Int) {
    getHouses(after: $after, first: $first) {
      totalCount
      edges {
        node {
          id
          displayName
          demo
          images {
            location
            displayName
            type
            Key
          }
          createdBy {
            id
            photoUrl
            displayName
          }
          architect {
            name
          }
          team {
            id
            displayName
            photoUrl
          }
          createdAt
          updatedAt
          source
          accProjectDetails {
            ${accProjectDetailsType}
          }
        }
        cursor
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_ALL_HOUSES = gql`
  query {
    getAllHouses {
      id
      displayName
      organizationId
      location
      description
    }
  }
`;

export const GET_HOUSE_VIEWS = gql`
  query GetHouseViews($houseId: String!) {
    getHouseViews(houseId: $houseId) {
      id
      house
      viewGuid
      displayName
      updatedAt
      createdAt
      bom {
        bomLoc {
          Bucket
          ETag
          Key
        }
        status
      }
    }
  }
`;

export const GET_COMPOUND_LAYER = gql`
  query GetCompoundLayer($id: ID!) {
    getCompoundLayer(id: $id) {
      category
      elementid
      uniqueId
      layers {
        function
        material
        thickness
        volume
      }
    }
  }
`;